import React, { useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { httpsify } from '../utils/url';
import { getAssetPlaybackUrl } from '../data/uploadService';
import { movieClips } from '../utils/movie';
import { UploadTrack, YouTubeTrack } from './AudioTracks';

// Resolves the best hero source: the published HLS stream if ready, else the
// movie's first clip (CDN url or presigned raw key). `published` flags the
// baked stream (whose audio already contains uploaded songs + original).
async function resolveHeroSource(movie) {
  if (movie.render?.status === 'ready' && movie.streamUrl) {
    return { url: httpsify(movie.streamUrl), hls: true, published: true };
  }
  const first = movieClips(movie)[0];
  if (!first) return { url: '', hls: false, published: false };
  if (first.url) return { url: httpsify(first.url), hls: /\.m3u8(\?|$)/.test(first.url), published: false };
  if (first.s3Key) {
    try {
      return { url: await getAssetPlaybackUrl(first.s3Key), hls: false, published: false };
    } catch {
      return { url: '', hls: false, published: false };
    }
  }
  return { url: '', hls: false, published: false };
}

// Plays a muted, looped segment of the movie behind the hero copy, kicking in
// after a configurable delay. Falls back to a still poster otherwise.
export default function HeroPreview({ movie }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const clockRef = useRef({ time: 0, playing: false, master: 1, mode: 'original' });
  const [armed, setArmed] = useState(false);
  const [ready, setReady] = useState(false);
  const [muted, setMuted] = useState(true);
  const [src, setSrc] = useState(null);

  // Soundtrack overlay synced to the segment. Published streams already bake in
  // uploaded songs + original audio, so we only overlay the cloud (YouTube)
  // tracks there; for raw previews we overlay everything the movie defines.
  const overlaySongs = useMemo(() => {
    const all = movie.songs || [];
    if (src?.published) return all.filter((s) => s.source !== 'upload');
    return all;
  }, [movie.songs, src]);
  const uploadSongs = useMemo(() => overlaySongs.filter((s) => s.source === 'upload' && s.url), [overlaySongs]);
  const ytSongs = useMemo(() => overlaySongs.filter((s) => s.source === 'youtube' && s.videoId), [overlaySongs]);

  const hero = movie.hero || {};
  const delaySec = hero.delaySec == null ? 4 : hero.delaySec;
  const startSec = hero.startSec || 0;
  const durationSec = hero.durationSec || 12;
  const poster = httpsify(hero.posterUrl || movie.heroUrl || movie.thumbUrl);

  // Arm autoplay after the configured delay.
  useEffect(() => {
    setArmed(false);
    setReady(false);
    const t = setTimeout(() => setArmed(true), Math.max(0, delaySec * 1000));
    return () => clearTimeout(t);
  }, [movie.id, delaySec]);

  // Resolve the source once we're armed.
  useEffect(() => {
    if (!armed) return undefined;
    let cancelled = false;
    (async () => {
      const s = await resolveHeroSource(movie);
      if (!cancelled) setSrc(s.url ? s : null);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, movie.id]);

  // Attach the source and loop the segment.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src || !src.url) return undefined;
    const segEnd = startSec + durationSec;
    const onLoaded = () => {
      try {
        video.currentTime = startSec;
      } catch {
        /* noop */
      }
      video.play().then(() => setReady(true)).catch(() => {});
    };
    const onTime = () => {
      if (video.currentTime >= segEnd || video.currentTime < startSec - 0.5) {
        try {
          video.currentTime = startSec;
        } catch {
          /* noop */
        }
      }
    };
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('timeupdate', onTime);

    let hls;
    if (src.hls && !video.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, capLevelToPlayerSize: true });
      hlsRef.current = hls;
      hls.loadSource(src.url);
      hls.attachMedia(video);
    } else {
      video.src = src.url;
      video.load();
    }
    return () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('timeupdate', onTime);
      if (hls) {
        hls.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, startSec, durationSec]);

  // Drive the shared audio clock from the hero video's position. Songs play
  // only while the preview is actually playing AND the viewer has unmuted.
  useEffect(() => {
    let raf;
    const loop = () => {
      const v = videoRef.current;
      const t = v ? v.currentTime : 0;
      const active = !!v && !v.paused && !muted;
      clockRef.current = {
        time: t,
        playing: active,
        master: 1,
        mode: muted ? 'original' : 'composed',
        dur: v && Number.isFinite(v.duration) ? v.duration : 0,
      };
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [muted]);

  const showVideo = armed && ready && src;

  return (
    <div className="cs-hero-media">
      {showVideo && uploadSongs.map((s) => (
        <UploadTrack key={s.id} song={s} clockRef={clockRef} />
      ))}
      {showVideo && ytSongs.map((s) => (
        <YouTubeTrack key={`${s.id}:${s.videoId}`} song={s} clockRef={clockRef} />
      ))}
      {poster && (
        <div
          className="cs-hero-poster"
          style={{ backgroundImage: `url(${poster})`, opacity: showVideo ? 0 : 1 }}
        />
      )}
      {armed && src && (
        <video
          ref={videoRef}
          className="cs-hero-video"
          muted={muted}
          loop={false}
          playsInline
          preload="auto"
          style={{ opacity: showVideo ? 1 : 0 }}
        />
      )}
      {showVideo && (
        <button
          className="cs-hero-mute"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const v = videoRef.current;
            const next = !muted;
            setMuted(next);
            if (v) {
              v.muted = next;
              if (!next) {
                v.volume = 1;
                // A click is a valid user gesture — (re)play to let audio through.
                v.play().catch(() => {});
              }
            }
          }}
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? '🔇' : '🔊'}
        </button>
      )}
    </div>
  );
}
